import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Loader2, LayoutDashboard, BookOpen, HelpCircle, Plug, Gauge,
  Sparkles, CheckCircle2, XCircle, Search, Plus, Sliders, Building2,
  AlertTriangle, ExternalLink, Archive, Send, Info, Zap, ShieldCheck,
  ClipboardCheck, Rocket, X,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import {
  wtcBootstrap, wtcBackfill, wtcProfile, wtcUpdateProfile,
  wtcKnowledge, wtcCreateKnowledge, wtcUpdateKnowledge,
  wtcApproveKnowledge, wtcArchiveKnowledge,
  wtcGaps, wtcAnswerGap, wtcDismissGap,
  wtcIntegrations, wtcAddIntegration, wtcRemoveIntegration, wtcTestIntegration,
  wtcUsage, wtcEntitlements, wtcSetEntitlement, wtcActionCenter,
  wtcRunAudit, wtcAuditFindings, wtcUpdateAuditFinding,
  wtcTrustProfile, wtcUpdateTrustProfile, wtcRunTrustChecks,
} from "@/lib/api";

const field = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
const STATUS_COLOR = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  in_review: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  expired: "bg-orange-50 text-orange-700 border-orange-200",
  archived: "bg-slate-100 text-slate-500 border-slate-200",
};
const URG_COLOR = { high: "border-rose-300 bg-rose-50 text-rose-700", medium: "border-amber-300 bg-amber-50 text-amber-700", low: "border-slate-200 bg-slate-50 text-slate-600" };
const fmtRelative = (s) => {
  if (!s) return "—";
  const diff = (Date.now() - new Date(s).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export default function WebsiteTechCenter() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || "action");
  const [wizard, setWizard] = useState(false);
  useEffect(() => {
    if (user) wtcBootstrap().then((b) => {
      if (b?.profile?.onboarding_status === "pending") setWizard(true);
    }).catch(() => {});
  }, [user]);
  useEffect(() => { if (sp.get("tab") !== tab) setSp({ tab }, { replace: true }); /* eslint-disable-next-line */ }, [tab]);

  if (user === undefined) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (user === null) {
    return (
      <div className="min-h-screen flex flex-col"><Navbar />
        <div className="flex-1 grid place-items-center px-6">
          <div className="max-w-md text-center" data-testid="wtc-login-gate">
            <LayoutDashboard className="w-10 h-10 text-indigo-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">WebsiteTech Center</h1>
            <p className="text-slate-600 mb-6">Business profile, unified knowledge, unanswered questions, integrations, and today's actions — all in one place.</p>
            <Link to="/login" className="inline-block px-6 py-3 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700" data-testid="wtc-login-link">Sign in</Link>
          </div>
        </div><Footer />
      </div>
    );
  }
  const tabs = [
    ["action", "Action Center", Zap],
    ["profile", "Business Profile", Building2],
    ["knowledge", "Knowledge Center", BookOpen],
    ["gaps", "Unanswered Questions", HelpCircle],
    ["audit", "Growth Audit", ClipboardCheck],
    ["trust", "Trust Profile", ShieldCheck],
    ["integrations", "Integrations", Plug],
    ["usage", "Usage & Plan", Gauge],
    ["entitlements", "Feature Access", Sliders],
  ];
  return (
    <div className="min-h-screen flex flex-col bg-slate-50"><Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 pt-28 pb-12" data-testid="wtc-page">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">WebsiteTech Center</h1>
            <p className="text-slate-600 mt-1 text-sm">Business profile • unified knowledge • unanswered questions • integrations • plan usage — the foundation every module reuses.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setWizard(true)} data-testid="wtc-open-wizard" className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 text-white px-3 py-2 text-xs font-semibold hover:bg-indigo-700"><Rocket className="w-3.5 h-3.5" /> Setup wizard</button>
            <BackfillButton />
          </div>
        </div>
        <div className="flex gap-1 mb-6 border-b border-slate-200 overflow-x-auto">
          {tabs.map(([k, l, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`wtc-tab-${k}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === k ? "border-indigo-600 text-indigo-600" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <Icon className="w-4 h-4" /> {l}
            </button>
          ))}
        </div>
        {tab === "action" && <ActionCenterTab />}
        {tab === "profile" && <ProfileTab />}
        {tab === "knowledge" && <KnowledgeTab />}
        {tab === "gaps" && <GapsTab />}
        {tab === "audit" && <AuditTab />}
        {tab === "trust" && <TrustTab />}
        {tab === "integrations" && <IntegrationsTab />}
        {tab === "usage" && <UsageTab />}
        {tab === "entitlements" && <EntitlementsTab />}
        {wizard && <OnboardingWizard onClose={() => setWizard(false)} />}
      </main><Footer />
    </div>
  );
}

function BackfillButton() {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    setBusy(true); setRes(null);
    try { const r = await wtcBackfill(); setRes(r); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      {res && <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1" data-testid="wtc-backfill-result">Imported: {Object.entries(res.imported).map(([k, v]) => `${v} ${k}`).join(" · ")}</span>}
      <button onClick={run} disabled={busy} data-testid="wtc-backfill-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-white px-3 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Sync existing knowledge
      </button>
    </div>
  );
}

// ============================================================================
// Action Center
// ============================================================================
function ActionCenterTab() {
  const [d, setD] = useState(null);
  useEffect(() => { wtcActionCenter().then(setD).catch(() => setD({ actions: [], tiles: {} })); }, []);
  if (!d) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  const tiles = [
    ["Hot leads", d.tiles.hot_leads, "text-rose-600"],
    ["Overdue tasks", d.tiles.overdue_tasks, "text-orange-600"],
    ["Unanswered Q's", d.tiles.unanswered_questions, "text-amber-600"],
    ["Pending approvals", d.tiles.pending_approvals, "text-indigo-600"],
    ["Failed integrations", d.tiles.failed_integrations, "text-rose-600"],
    ["KB in review", d.tiles.kb_in_review, "text-slate-600"],
    ["KB expiring soon", d.tiles.kb_expiring_30d, "text-amber-600"],
    ["Usage warnings", d.tiles.usage_warnings, "text-orange-600"],
  ];
  return (
    <div className="space-y-6" data-testid="wtc-action-center">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
        {tiles.map(([l, v, c]) => (
          <div key={l} className="rounded-xl border border-slate-200 bg-white p-3">
            <div className={`text-xl font-bold ${c}`} data-testid={`ac-tile-${l.split(' ')[0].toLowerCase()}`}>{v || 0}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">{l}</div>
          </div>
        ))}
      </div>
      <section className="rounded-2xl border border-slate-200 bg-white p-5">
        <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><Zap className="w-4 h-4 text-indigo-600" /> Today's actions</h2>
        {d.actions.length === 0 ? <div className="text-sm text-slate-500 py-6 text-center">All caught up. No prioritized actions right now.</div> :
          <div className="space-y-2">
            {d.actions.map((a) => (
              <div key={a.id} className="rounded-lg border border-slate-200 p-3 flex items-start gap-3 hover:border-indigo-300 transition-colors" data-testid={`action-${a.id}`}>
                <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border ${URG_COLOR[a.urgency] || URG_COLOR.low}`}>{a.urgency}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate">{a.title}</div>
                  <div className="text-xs text-slate-500 truncate">{a.evidence}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5 flex gap-3">
                    <span>impact: <strong>{a.impact}</strong></span>
                    <span>confidence: <strong>{a.confidence}</strong></span>
                    <span>effort: <strong>{a.effort}</strong></span>
                    {a.owner && <span>owner: <strong>{a.owner}</strong></span>}
                  </div>
                </div>
                {a.link && <Link to={a.link} className="shrink-0 text-xs text-indigo-600 hover:text-indigo-800 font-semibold">Take action →</Link>}
              </div>
            ))}
          </div>}
      </section>
    </div>
  );
}

// ============================================================================
// Business Profile
// ============================================================================
function ProfileTab() {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => { wtcProfile().then(setP).catch(() => setP(null)); }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setBusy(true); setMsg("");
    try {
      const payload = { ...p,
        services: Array.isArray(p.services) ? p.services : (p.services || "").split(",").map((s) => s.trim()).filter(Boolean),
        languages: Array.isArray(p.languages) ? p.languages : (p.languages || "").split(",").map((s) => s.trim()).filter(Boolean),
        channels: Array.isArray(p.channels) ? p.channels : (p.channels || "").split(",").map((s) => s.trim()).filter(Boolean),
        goals: Array.isArray(p.goals) ? p.goals : (p.goals || "").split(",").map((s) => s.trim()).filter(Boolean),
        competitors: Array.isArray(p.competitors) ? p.competitors : (p.competitors || "").split(",").map((s) => s.trim()).filter(Boolean),
      };
      const r = await wtcUpdateProfile(payload);
      setP(r); setMsg("Saved ✓");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) { setMsg(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  if (!p) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  const F = (label, key, kind = "text") => (
    <div>
      <label className="text-xs font-semibold text-slate-600 block mb-1">{label}</label>
      {kind === "textarea" ?
        <textarea rows={3} className={field} value={p[key] || ""} onChange={(e) => setP({ ...p, [key]: e.target.value })} data-testid={`bp-${key}`} /> :
        <input className={field} value={Array.isArray(p[key]) ? p[key].join(", ") : (p[key] || "")} onChange={(e) => setP({ ...p, [key]: e.target.value })} data-testid={`bp-${key}`} />}
    </div>
  );
  return (
    <div className="space-y-4 max-w-3xl" data-testid="wtc-profile">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          {F("Business name", "business_name")}
          {F("Industry", "industry")}
          {F("Website", "website")}
          {F("Plan", "plan")}
        </div>
        {F("Services (comma-separated)", "services")}
        <div className="grid grid-cols-2 gap-3">
          {F("Languages", "languages")}
          {F("Channels", "channels")}
        </div>
        {F("Goals", "goals")}
        {F("Target customers", "target_customers", "textarea")}
        {F("Competitors", "competitors")}
        {F("Brand voice", "brand_voice", "textarea")}
        {F("Escalation rules", "escalation_rules", "textarea")}
        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <div className="text-xs text-slate-500">Onboarding: <strong>{p.onboarding_status}</strong> · step {p.onboarding_step}</div>
          <div className="flex items-center gap-2">
            {msg && <span className="text-xs text-emerald-700">{msg}</span>}
            <button onClick={save} disabled={busy} data-testid="bp-save" className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Save profile</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Knowledge Center
// ============================================================================
function KnowledgeTab() {
  const [d, setD] = useState(null);
  const [status, setStatus] = useState("");
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const load = useCallback(() => {
    wtcKnowledge({ ...(status ? { status } : {}), ...(kind ? { kind } : {}), ...(q ? { q } : {}) }).then(setD).catch(() => setD({ knowledge: [], counts: {}, kinds: [], statuses: [] }));
  }, [status, kind, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  if (!d) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="space-y-4" data-testid="wtc-knowledge">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {["draft", "in_review", "approved", "expired", "archived"].map((s) => (
          <button key={s} onClick={() => setStatus(status === s ? "" : s)} data-testid={`kb-count-${s}`}
            className={`rounded-lg border p-3 text-left ${status === s ? "ring-2 ring-indigo-500" : ""} ${STATUS_COLOR[s]}`}>
            <div className="text-xl font-bold">{d.counts[s] || 0}</div>
            <div className="text-[11px] font-medium capitalize">{s.replace("_", " ")}</div>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input className={field + " pl-9"} placeholder="Search knowledge…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="kb-search" />
        </div>
        <select className={field + " max-w-[160px]"} value={kind} onChange={(e) => setKind(e.target.value)} data-testid="kb-kind-filter">
          <option value="">All kinds</option>
          {d.kinds.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <button onClick={() => setShowNew(true)} data-testid="kb-new-btn" className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 text-white px-3 py-2 text-sm font-semibold hover:bg-indigo-700"><Plus className="w-4 h-4" /> New</button>
      </div>
      {d.knowledge.length === 0 ? <div className="text-sm text-slate-500 py-16 text-center rounded-2xl border border-dashed border-slate-200 bg-white">No knowledge yet. Click "Sync existing knowledge" above to import FAQ, prompts and templates from your existing setup.</div> :
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          {d.knowledge.map((k) => (
            <KBRow key={k.id} item={k} onOpen={() => setSelected(k.id)} onChange={load} />
          ))}
        </div>}
      {showNew && <NewKBModal kinds={d.kinds} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {selected && <KBDetail kid={selected} onClose={() => setSelected(null)} onChange={load} />}
    </div>
  );
}

function KBRow({ item, onOpen, onChange }) {
  const [busy, setBusy] = useState(false);
  const approve = async (e) => { e.stopPropagation(); setBusy(true); try { await wtcApproveKnowledge(item.id); onChange(); } finally { setBusy(false); } };
  const archive = async (e) => { e.stopPropagation(); if (!window.confirm("Archive?")) return; setBusy(true); try { await wtcArchiveKnowledge(item.id); onChange(); } finally { setBusy(false); } };
  return (
    <button onClick={onOpen} className="w-full text-left px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 flex items-start gap-3" data-testid={`kb-row-${item.id}`}>
      <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border ${STATUS_COLOR[item.status]}`}>{item.status}</span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900 truncate">{item.title} <span className="text-slate-400 text-xs font-normal">· {item.kind} · v{item.version}</span></div>
        <div className="text-xs text-slate-500 line-clamp-1">{item.body}</div>
        <div className="text-[10px] text-slate-400 mt-0.5">Updated {fmtRelative(item.updated_at)}{item.owner_id ? ` · ${item.owner_id}` : ""}</div>
      </div>
      <div className="flex gap-1 shrink-0">
        {item.status !== "approved" && item.status !== "archived" && (
          <button onClick={approve} disabled={busy} data-testid={`kb-approve-${item.id}`} className="text-xs px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Approve</button>
        )}
        {item.status !== "archived" && (
          <button onClick={archive} disabled={busy} data-testid={`kb-archive-${item.id}`} className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50"><Archive className="w-3 h-3" /></button>
        )}
      </div>
    </button>
  );
}

function NewKBModal({ kinds, onClose, onSaved }) {
  const [f, setF] = useState({ kind: "faq", title: "", body: "", tags: "", status: "draft" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    if (!f.title.trim() || !f.body.trim()) { setErr("Title and body required"); return; }
    setBusy(true); setErr("");
    try {
      await wtcCreateKnowledge({ ...f, tags: f.tags.split(",").map((s) => s.trim()).filter(Boolean) });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 grid place-items-center px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-2" onClick={(e) => e.stopPropagation()} data-testid="new-kb-modal">
        <h3 className="text-lg font-bold text-slate-900">New knowledge source</h3>
        <select className={field} value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="nkb-kind">
          {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <input className={field} placeholder="Title / question" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="nkb-title" />
        <textarea rows={6} className={field} placeholder="Body / answer" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="nkb-body" />
        <input className={field} placeholder="Tags (comma-separated)" value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} data-testid="nkb-tags" />
        <select className={field} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="nkb-status">
          <option value="draft">Draft</option><option value="in_review">In review</option><option value="approved">Approved (skip review)</option>
        </select>
        {err && <div className="text-xs text-rose-600">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="nkb-save" className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Save</button>
        </div>
      </div>
    </div>
  );
}

function KBDetail({ kid, onClose, onChange }) {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ title: "", body: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    import("@/lib/api").then((m) => m.wtcKnowledgeItem(kid)).then((r) => {
      setD(r); setForm({ title: r.knowledge.title, body: r.knowledge.body });
    });
  }, [kid]);
  const save = async () => {
    setBusy(true);
    try { await wtcUpdateKnowledge(kid, form); onChange(); onClose(); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 grid place-items-center px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={`kb-detail-${kid}`}>
        {!d ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> :
          <>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-slate-500">{d.knowledge.kind} · v{d.knowledge.version} · <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_COLOR[d.knowledge.status]}`}>{d.knowledge.status}</span></div>
              </div>
              <button onClick={() => setEdit((e) => !e)} className="text-xs text-indigo-600" data-testid="kbd-edit-toggle">{edit ? "Cancel edit" : "Edit"}</button>
            </div>
            {edit ? (
              <>
                <input className={field} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="kbd-title" />
                <textarea rows={10} className={field} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="kbd-body" />
                <div className="text-xs text-amber-600">Editing body will reset status to Draft and require re-approval.</div>
                <div className="flex justify-end gap-2"><button onClick={onClose} className="px-3 py-1.5 text-sm">Close</button><button onClick={save} disabled={busy} data-testid="kbd-save" className="px-3 py-1.5 bg-indigo-600 text-white rounded text-sm">Save</button></div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-bold text-slate-900">{d.knowledge.title}</h3>
                <div className="whitespace-pre-wrap text-sm text-slate-700 rounded-lg bg-slate-50 border border-slate-200 p-3">{d.knowledge.body}</div>
                <div className="text-xs text-slate-500">
                  <div>Tags: {(d.knowledge.tags || []).join(", ") || "—"}</div>
                  <div>Owner: {d.knowledge.owner_id || "—"} · Approver: {d.knowledge.approver_id || "—"}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase mb-1">Version history ({d.versions.length})</div>
                  <div className="space-y-1">
                    {d.versions.map((v) => (
                      <div key={v.id} className="text-xs text-slate-600">v{v.version} · {fmtRelative(v.at)} · {v.actor_id || "system"} · {v.status}</div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </>}
      </div>
    </div>
  );
}

// ============================================================================
// Unanswered Question Intelligence
// ============================================================================
function GapsTab() {
  const [d, setD] = useState(null);
  const [status, setStatus] = useState("open");
  const [answering, setAnswering] = useState(null);
  const load = useCallback(() => { wtcGaps({ status }).then(setD).catch(() => setD({ gaps: [], stats: {} })); }, [status]);
  useEffect(() => { load(); }, [load]);
  const dismiss = async (gid) => { await wtcDismissGap(gid); load(); };
  if (!d) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="space-y-4" data-testid="wtc-gaps">
      <div className="flex gap-2 items-center">
        {["open", "answered", "dismissed"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`gap-filter-${s}`}
            className={`text-xs px-3 py-1.5 rounded-full border ${status === s ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
            {s} ({d.stats[s] || 0})
          </button>
        ))}
      </div>
      <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-3 text-xs text-indigo-900 flex items-start gap-2"><Info className="w-4 h-4 mt-0.5 shrink-0" /><span>Every time an AI assistant falls back or gives an "I don't know" answer, we record the visitor's question here. Answering one adds it to the Knowledge Center so the AI can respond next time.</span></div>
      {d.gaps.length === 0 ? <div className="text-sm text-slate-500 py-16 text-center rounded-2xl border border-dashed border-slate-200 bg-white">No unanswered questions in this bucket.</div> :
        <div className="space-y-2">
          {d.gaps.map((g) => (
            <div key={g.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`gap-${g.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900">"{g.question}"</div>
                  <div className="text-xs text-slate-500 mt-1">Asked {g.count}× · Sources: {(g.sources || []).join(", ") || "—"} · Last seen {fmtRelative(g.last_seen)}</div>
                  {g.variants?.length > 1 && <div className="text-[11px] text-slate-400 mt-1">Also asked as: {g.variants.slice(1, 4).map((v) => `"${v}"`).join(", ")}</div>}
                </div>
                {g.status === "open" && (
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => setAnswering(g)} data-testid={`gap-answer-${g.id}`} className="text-xs px-3 py-1.5 rounded bg-indigo-600 text-white font-semibold hover:bg-indigo-700 inline-flex items-center gap-1"><Send className="w-3 h-3" /> Answer</button>
                    <button onClick={() => dismiss(g.id)} data-testid={`gap-dismiss-${g.id}`} className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50">Dismiss</button>
                  </div>
                )}
                {g.status === "answered" && <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">Answered</span>}
              </div>
            </div>
          ))}
        </div>}
      {answering && <AnswerGapModal gap={answering} onClose={() => setAnswering(null)} onSaved={() => { setAnswering(null); load(); }} />}
    </div>
  );
}

function AnswerGapModal({ gap, onClose, onSaved }) {
  const [body, setBody] = useState("");
  const [approve, setApprove] = useState(true);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try { await wtcAnswerGap(gap.id, { body, approve_now: approve, kind: "faq" }); onSaved(); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 grid place-items-center px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="answer-gap-modal">
        <h3 className="text-lg font-bold text-slate-900">Answer this question</h3>
        <div className="text-sm text-slate-700 italic border-l-4 border-indigo-500 pl-3">"{gap.question}"</div>
        <textarea rows={6} className={field} placeholder="Write the answer the AI should give next time…" value={body} onChange={(e) => setBody(e.target.value)} data-testid="ag-body" />
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" checked={approve} onChange={(e) => setApprove(e.target.checked)} data-testid="ag-approve" /> Approve now (AI will use this immediately)
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button onClick={save} disabled={busy || !body.trim()} data-testid="ag-save" className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Save answer</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Integrations
// ============================================================================
function IntegrationsTab() {
  const [d, setD] = useState(null);
  const load = useCallback(() => { wtcIntegrations().then(setD).catch(() => setD({ integrations: [], detected: [], kinds: [] })); }, []);
  useEffect(() => { load(); }, [load]);
  const add = async (kind) => { await wtcAddIntegration({ kind, status: "connected" }); load(); };
  const remove = async (kind) => { if (!window.confirm(`Disconnect ${kind}?`)) return; await wtcRemoveIntegration(kind); load(); };
  const test = async (kind) => { await wtcTestIntegration(kind); load(); };
  if (!d) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="space-y-4" data-testid="wtc-integrations">
      {d.detected.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
          <div className="text-xs font-semibold text-amber-900 mb-2">Auto-detected connections not yet registered — click to add:</div>
          <div className="flex flex-wrap gap-2">
            {d.detected.map((i) => (
              <button key={i.kind} onClick={() => add(i.kind)} data-testid={`intg-detected-${i.kind}`} className="text-xs px-2 py-1 rounded bg-white border border-amber-300 hover:bg-amber-100">
                + Add {i.kind}
              </button>
            ))}
          </div>
        </div>
      )}
      {d.integrations.length === 0 ? <div className="text-sm text-slate-500 py-16 text-center rounded-2xl border border-dashed border-slate-200 bg-white">No integrations registered yet.</div> :
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          {d.integrations.map((i) => (
            <div key={i.id} className="px-4 py-3 border-b border-slate-100 last:border-b-0 flex items-center gap-3" data-testid={`intg-${i.id}`}>
              <div className="w-9 h-9 rounded bg-slate-100 grid place-items-center"><Plug className="w-4 h-4 text-slate-600" /></div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900">{i.kind}</div>
                <div className="text-xs text-slate-500">{i.status} · Last sync {fmtRelative(i.last_sync_at)} · Health: <span className={i.health_status === "ok" ? "text-emerald-600" : "text-rose-600"}>{i.health_status}</span>{i.last_error ? ` · ${i.last_error}` : ""}</div>
              </div>
              <button onClick={() => test(i.kind)} data-testid={`intg-test-${i.kind}`} className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50">Test</button>
              <button onClick={() => remove(i.kind)} data-testid={`intg-remove-${i.kind}`} className="text-xs px-2 py-1 text-slate-400 hover:text-rose-600">Remove</button>
            </div>
          ))}
        </div>}
    </div>
  );
}

// ============================================================================
// Usage & Plan
// ============================================================================
function UsageTab() {
  const [d, setD] = useState(null);
  useEffect(() => { wtcUsage().then(setD).catch(() => setD(null)); }, []);
  if (!d) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="space-y-4" data-testid="wtc-usage">
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="text-xs text-slate-500">Period: {d.period} · Plan: <strong className="text-slate-800">{d.plan}</strong></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {Object.entries(d.metrics).map(([m, v]) => (
          <div key={m} className={`rounded-xl border p-4 ${v.over ? "border-rose-300 bg-rose-50" : v.pct >= 80 ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`} data-testid={`meter-${m}`}>
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold text-slate-600 uppercase">{m.replace("_", " ")}</div>
              {v.over && <AlertTriangle className="w-4 h-4 text-rose-600" />}
            </div>
            <div className="text-2xl font-bold text-slate-900 mt-1">{(v.used || 0).toLocaleString()}<span className="text-sm font-normal text-slate-500"> / {(v.limit || 0).toLocaleString()}</span></div>
            <div className="h-1.5 rounded-full bg-slate-200 mt-2 overflow-hidden"><div className={`h-full ${v.over ? "bg-rose-600" : v.pct >= 80 ? "bg-amber-500" : "bg-indigo-500"}`} style={{ width: `${Math.min(100, v.pct)}%` }} /></div>
            <div className="text-[11px] text-slate-500 mt-1">{v.pct}% used</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Feature Entitlements
// ============================================================================
function EntitlementsTab() {
  const [d, setD] = useState(null);
  const load = useCallback(() => { wtcEntitlements().then(setD).catch(() => setD({ entitlements: [] })); }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (feature, enabled) => { await wtcSetEntitlement({ feature, enabled }); load(); };
  if (!d) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="space-y-3" data-testid="wtc-entitlements">
      <div className="text-xs text-slate-500 flex items-center gap-1"><Info className="w-3 h-3" /> Turn any optional module on or off per tenant. Modules default to on; disabling hides UI and blocks API access.</div>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {d.entitlements.map((e) => (
          <div key={e.feature} className="px-4 py-3 border-b border-slate-100 last:border-b-0 flex items-center gap-3" data-testid={`ent-${e.feature}`}>
            <label className="flex items-center gap-2 cursor-pointer flex-1">
              <input type="checkbox" checked={e.enabled} onChange={(ev) => toggle(e.feature, ev.target.checked)} data-testid={`ent-toggle-${e.feature}`} className="w-4 h-4" />
              <span className="text-sm font-semibold text-slate-900">{e.feature.replace(/_/g, " ")}</span>
              {e.default && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">default</span>}
            </label>
            <span className={`text-xs px-2 py-1 rounded ${e.enabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{e.enabled ? "enabled" : "disabled"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Growth Audit
// ============================================================================
function AuditTab() {
  const [d, setD] = useState(null);
  const [url, setUrl] = useState("");
  const [running, setRunning] = useState(false);
  const [statusFilter, setStatusFilter] = useState("open");
  const [msg, setMsg] = useState("");
  const load = useCallback(() => {
    wtcAuditFindings({ status: statusFilter }).then(setD).catch(() => setD({ findings: [], counts: {} }));
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { wtcProfile().then((p) => { if (p?.website && !url) setUrl(p.website); }).catch(() => {}); /* eslint-disable-next-line */ }, []);
  const run = async () => {
    if (!url.trim()) { setMsg("Enter a URL"); return; }
    setRunning(true); setMsg("");
    try {
      const r = await wtcRunAudit({ website: url });
      setMsg(`Found ${r.findings_count} findings on ${r.website || url}`);
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Audit failed"); }
    finally { setRunning(false); }
  };
  const resolve = async (fid, status) => { await wtcUpdateAuditFinding(fid, { status }); load(); };
  const CATEGORY_COLOR = {
    seo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    conversion: "bg-emerald-50 text-emerald-700 border-emerald-200",
    security: "bg-rose-50 text-rose-700 border-rose-200",
    accessibility: "bg-amber-50 text-amber-700 border-amber-200",
    mobile: "bg-sky-50 text-sky-700 border-sky-200",
    local_search: "bg-violet-50 text-violet-700 border-violet-200",
    technical: "bg-slate-50 text-slate-700 border-slate-200",
  };
  if (!d) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="space-y-4" data-testid="wtc-audit">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
        <div className="text-xs text-slate-500 flex items-center gap-1"><Info className="w-3 h-3" /> Scan any URL for technical SEO, conversion, accessibility, mobile, security, and local-search readiness. Findings flow into the Action Center.</div>
        <div className="flex flex-wrap gap-2">
          <input className={field + " flex-1 min-w-[220px]"} placeholder="https://your-domain.com" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="audit-url" />
          <button onClick={run} disabled={running || !url.trim()} data-testid="audit-run" className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />} Run growth audit
          </button>
        </div>
        {msg && <div className="text-xs text-slate-600" data-testid="audit-msg">{msg}</div>}
      </div>
      <div className="flex gap-2">
        {["open", "resolved", "dismissed"].map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)} data-testid={`audit-filter-${s}`}
            className={`text-xs px-3 py-1.5 rounded-full border ${statusFilter === s ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
            {s} ({d.counts[s] || 0})
          </button>
        ))}
      </div>
      {d.findings.length === 0 ? <div className="text-sm text-slate-500 py-16 text-center rounded-2xl border border-dashed border-slate-200 bg-white">No findings in this bucket. Run an audit to populate.</div> :
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          {d.findings.map((f) => (
            <div key={f.id} className="px-4 py-3 border-b border-slate-100 last:border-b-0 flex items-start gap-3" data-testid={`finding-${f.id}`}>
              <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border ${CATEGORY_COLOR[f.category] || CATEGORY_COLOR.technical}`}>{f.category}</span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900">{f.title}</div>
                <div className="text-xs text-slate-500 mt-0.5">{f.evidence}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">impact: <strong>{f.impact}</strong> · confidence: <strong>{f.confidence}</strong> · effort: <strong>{f.effort}</strong> · {f.website}</div>
              </div>
              {statusFilter === "open" && (
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => resolve(f.id, "resolved")} data-testid={`finding-resolve-${f.id}`} className="text-xs px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Resolved</button>
                  <button onClick={() => resolve(f.id, "dismissed")} data-testid={`finding-dismiss-${f.id}`} className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50">Dismiss</button>
                </div>
              )}
            </div>
          ))}
        </div>}
    </div>
  );
}

// ============================================================================
// Trust Profile & Continuous Controls
// ============================================================================
function TrustTab() {
  const [d, setD] = useState(null);
  const [url, setUrl] = useState("");
  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => {
    wtcTrustProfile().then((r) => { setD(r); setEdit(r.profile); }).catch(() => setD(null));
    wtcProfile().then((p) => { if (p?.website && !url) setUrl(p.website); }).catch(() => {});
  }, [url]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const run = async () => {
    if (!url.trim()) { setMsg("Enter a URL"); return; }
    setRunning(true); setMsg("");
    try {
      const r = await wtcRunTrustChecks({ website: url });
      const passed = Object.values(r.checks || {}).filter(Boolean).length;
      const total = Object.keys(r.checks || {}).length;
      setMsg(`Ran ${total} controls on ${r.website} — ${passed} pass, ${total - passed} fail`);
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Check failed"); }
    finally { setRunning(false); }
  };
  const saveProfile = async () => {
    setBusy(true);
    try { const r = await wtcUpdateTrustProfile(edit); setEdit(r); setMsg("Profile saved"); setTimeout(() => setMsg(""), 2000); }
    finally { setBusy(false); }
  };
  if (!d || !edit) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  const sites = Object.entries(d.controls_by_site || {});
  return (
    <div className="grid lg:grid-cols-[1fr_1fr] gap-4" data-testid="wtc-trust">
      <div className="space-y-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
          <div className="text-xs text-slate-500 flex items-start gap-1"><Info className="w-3 h-3 mt-0.5" /><span>Continuous website controls: SSL, security headers, policy links. Every check writes evidence + audit history. Not a legal certification.</span></div>
          <div className="flex gap-2">
            <input className={field + " flex-1"} placeholder="https://your-domain.com" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="trust-url" />
            <button onClick={run} disabled={running || !url.trim()} data-testid="trust-run" className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 text-white px-3 py-2 text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Run checks
            </button>
          </div>
          {msg && <div className="text-xs text-slate-600" data-testid="trust-msg">{msg}</div>}
        </div>
        {sites.length === 0 ? <div className="text-sm text-slate-500 py-8 text-center rounded-2xl border border-dashed border-slate-200 bg-white">No control results yet. Run a check above.</div> :
          sites.map(([site, controls]) => (
            <div key={site} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`trust-site-${site}`}>
              <div className="text-sm font-semibold text-slate-900 mb-2">{site}</div>
              <div className="grid grid-cols-2 gap-1.5">
                {controls.map((c) => (
                  <div key={c.id} className={`text-xs rounded border px-2 py-1.5 ${c.result ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-rose-200 bg-rose-50 text-rose-800"}`} data-testid={`control-${c.control}`}>
                    <div className="flex items-center gap-1 font-semibold">{c.result ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {c.control}</div>
                    <div className="text-[10px] opacity-80 truncate mt-0.5">{c.evidence}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-2">
        <h3 className="font-semibold text-slate-900 mb-1">Public trust profile</h3>
        <p className="text-xs text-slate-500 mb-2">Fields displayed on your public Trust Center page when visibility is set to public.</p>
        {[
          ["security_contact", "Security contact email"],
          ["privacy_contact", "Privacy contact email"],
          ["hosting", "Hosting summary"],
          ["data_processing", "Data processing summary"],
          ["cookie_disclosure", "Cookie & tracking disclosure"],
          ["ai_disclosure", "AI-use disclosure"],
          ["accessibility_statement", "Accessibility statement"],
          ["uptime_url", "Uptime / status URL"],
        ].map(([k, label]) => (
          <div key={k}>
            <label className="text-xs text-slate-500">{label}</label>
            {["hosting", "data_processing", "cookie_disclosure", "ai_disclosure", "accessibility_statement"].includes(k) ?
              <textarea rows={2} className={field} value={edit[k] || ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} data-testid={`trust-${k}`} /> :
              <input className={field} value={edit[k] || ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} data-testid={`trust-${k}`} />}
          </div>
        ))}
        <div>
          <label className="text-xs text-slate-500">Visibility</label>
          <select className={field} value={edit.visibility || "internal"} onChange={(e) => setEdit({ ...edit, visibility: e.target.value })} data-testid="trust-visibility">
            <option value="internal">Internal only</option>
            <option value="public">Public Trust Center</option>
          </select>
        </div>
        <div className="flex justify-end pt-2">
          <button onClick={saveProfile} disabled={busy} data-testid="trust-save" className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Save profile</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Onboarding Wizard — one guided flow that fills every module
// ============================================================================
function OnboardingWizard({ onClose }) {
  const [step, setStep] = useState(0);
  const [f, setF] = useState({
    business_name: "", industry: "", website: "",
    services: "", target_customers: "",
    languages: "en", channels: "email,sms",
    goals: "increase-leads,book-appointments",
    plan: "essentials",
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [auditPreview, setAuditPreview] = useState(null);
  useEffect(() => {
    wtcProfile().then((p) => { if (p) setF((prev) => ({ ...prev, ...p, services: (p.services || []).join(", "), languages: (p.languages || ["en"]).join(","), channels: (p.channels || ["email"]).join(","), goals: (p.goals || []).join(",") })); }).catch(() => {});
  }, []);
  const next = () => setStep((s) => Math.min(s + 1, 4));
  const back = () => setStep((s) => Math.max(s - 1, 0));
  const runAudit = async () => {
    if (!f.website) { setMsg("Add a website first"); return; }
    setBusy(true); setMsg("");
    try { const r = await wtcRunAudit({ website: f.website }); setAuditPreview(r); }
    catch (e) { setMsg(e?.response?.data?.detail || "Audit failed"); }
    finally { setBusy(false); }
  };
  const finish = async () => {
    setBusy(true); setMsg("");
    try {
      const payload = {
        business_name: f.business_name, industry: f.industry, website: f.website,
        services: f.services.split(",").map((s) => s.trim()).filter(Boolean),
        target_customers: f.target_customers,
        languages: f.languages.split(",").map((s) => s.trim()).filter(Boolean),
        channels: f.channels.split(",").map((s) => s.trim()).filter(Boolean),
        goals: f.goals.split(",").map((s) => s.trim()).filter(Boolean),
        plan: f.plan,
        onboarding_status: "complete", onboarding_step: 4,
      };
      await wtcUpdateProfile(payload);
      setMsg("Setup complete — welcome to WebsiteTech Center!");
      setTimeout(onClose, 1200);
    } catch (e) { setMsg(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const STEPS = ["Business", "Website", "Goals & channels", "Grow audit", "Confirm"];
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 grid place-items-center px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 space-y-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="onboarding-wizard">
        <div className="flex items-center justify-between">
          <div className="inline-flex items-center gap-2"><Rocket className="w-5 h-5 text-indigo-600" /><h3 className="text-lg font-bold text-slate-900">Setup wizard</h3></div>
          <button onClick={onClose} data-testid="wizard-close"><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <div className="flex items-center gap-1.5">
          {STEPS.map((s, i) => (
            <div key={s} className={`flex-1 h-1.5 rounded-full ${i <= step ? "bg-indigo-600" : "bg-slate-200"}`} data-testid={`wizard-step-bar-${i}`} />
          ))}
        </div>
        <div className="text-xs text-slate-500 uppercase font-semibold">Step {step + 1} of {STEPS.length}: {STEPS[step]}</div>

        {step === 0 && (
          <div className="space-y-3" data-testid="wizard-step-0">
            <label className="text-xs text-slate-500">Business name</label>
            <input className={field} value={f.business_name} onChange={(e) => setF({ ...f, business_name: e.target.value })} data-testid="wiz-name" />
            <label className="text-xs text-slate-500">Industry</label>
            <input className={field} placeholder="e.g. medical clinic, travel agency, contractor" value={f.industry} onChange={(e) => setF({ ...f, industry: e.target.value })} data-testid="wiz-industry" />
            <label className="text-xs text-slate-500">Who are your target customers?</label>
            <textarea rows={2} className={field} value={f.target_customers} onChange={(e) => setF({ ...f, target_customers: e.target.value })} data-testid="wiz-target" />
          </div>
        )}
        {step === 1 && (
          <div className="space-y-3" data-testid="wizard-step-1">
            <label className="text-xs text-slate-500">Website URL</label>
            <input className={field} placeholder="https://your-domain.com" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} data-testid="wiz-website" />
            <label className="text-xs text-slate-500">Services or products you offer (comma-separated)</label>
            <textarea rows={2} className={field} placeholder="Consulting, Website design, SEO" value={f.services} onChange={(e) => setF({ ...f, services: e.target.value })} data-testid="wiz-services" />
          </div>
        )}
        {step === 2 && (
          <div className="space-y-3" data-testid="wizard-step-2">
            <label className="text-xs text-slate-500">Primary goals (comma-separated)</label>
            <input className={field} value={f.goals} onChange={(e) => setF({ ...f, goals: e.target.value })} data-testid="wiz-goals" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500">Languages</label>
                <input className={field} placeholder="en,fr" value={f.languages} onChange={(e) => setF({ ...f, languages: e.target.value })} data-testid="wiz-langs" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Communication channels</label>
                <input className={field} placeholder="email,sms,whatsapp" value={f.channels} onChange={(e) => setF({ ...f, channels: e.target.value })} data-testid="wiz-channels" />
              </div>
            </div>
            <label className="text-xs text-slate-500">Plan</label>
            <select className={field} value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })} data-testid="wiz-plan">
              <option value="essentials">Essentials</option>
              <option value="growth">Growth</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
        )}
        {step === 3 && (
          <div className="space-y-3" data-testid="wizard-step-3">
            <p className="text-sm text-slate-600">Run a quick Growth Audit on <strong>{f.website || "your website"}</strong> to seed the Action Center with prioritized fixes.</p>
            <button onClick={runAudit} disabled={busy || !f.website} data-testid="wiz-audit-run" className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />} Run audit now</button>
            {auditPreview && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="wiz-audit-result">
                <div className="font-semibold text-slate-800 mb-1">Found {auditPreview.findings_count} issues on {auditPreview.website}</div>
                <ul className="text-xs text-slate-600 list-disc pl-5">
                  {(auditPreview.findings || []).slice(0, 5).map((f2, i) => <li key={i}>{f2.title}</li>)}
                </ul>
              </div>
            )}
            <p className="text-xs text-slate-500">You can skip this and run it later from the Growth Audit tab.</p>
          </div>
        )}
        {step === 4 && (
          <div className="space-y-3" data-testid="wizard-step-4">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4">
              <div className="text-sm font-semibold text-emerald-900 mb-1">Ready to launch</div>
              <div className="text-xs text-emerald-800">We'll save your profile and mark onboarding complete. You can update anything from the Business Profile tab.</div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3 text-xs text-slate-600 space-y-1">
              <div><strong>Business:</strong> {f.business_name} — {f.industry}</div>
              <div><strong>Website:</strong> {f.website || "—"}</div>
              <div><strong>Services:</strong> {f.services || "—"}</div>
              <div><strong>Languages:</strong> {f.languages}</div>
              <div><strong>Channels:</strong> {f.channels}</div>
              <div><strong>Plan:</strong> {f.plan}</div>
            </div>
          </div>
        )}
        {msg && <div className={`text-xs px-2 py-1 rounded ${msg.includes("complete") ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-700"}`} data-testid="wiz-msg">{msg}</div>}
        <div className="flex justify-between pt-2 border-t border-slate-100">
          <button onClick={back} disabled={step === 0} data-testid="wiz-back" className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-30">Back</button>
          {step < 4 ? (
            <button onClick={next} data-testid="wiz-next" className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700">Next →</button>
          ) : (
            <button onClick={finish} disabled={busy} data-testid="wiz-finish" className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Finish setup</button>
          )}
        </div>
      </div>
    </div>
  );
}
